<script setup lang="ts">
import { computed } from 'vue'
import type { WarningCalendarSummaryViewCalendarSummaryFragment } from './WarningCalendarSummaryView.generated'
import WarningCalendarSummaryViewListElement from './WarningCalendarSummaryViewListElement.vue'
import type { CalendarWarning } from './type'

const { calendarSummaries, linkedStaffGUIDs } = defineProps<{
  calendarSummaries: WarningCalendarSummaryViewCalendarSummaryFragment[]
  linkedStaffGUIDs: string[]
}>()

const calendarSummariesWithWarning = computed(() => {
  return calendarSummaries.reduce<
    {
      calendarSummary: WarningCalendarSummaryViewCalendarSummaryFragment
      warning: CalendarWarning
    }[]
  >((prev, calendarSummary) => {
    if (
      calendarSummary.assignedStaffs.length === 0 &&
      calendarSummary.status === 'ACTIVE'
    ) {
      return [
        ...prev,
        {
          warning: 'STAFF_NOT_ASSIGNED',
          calendarSummary,
        },
      ]
    }
    if (
      calendarSummary.assignedStaffs.some(staff => {
        return !linkedStaffGUIDs.includes(staff.guid)
      })
    ) {
      return [
        ...prev,
        {
          warning: 'NOT_LINKED',
          calendarSummary,
        },
      ]
    }
    return prev
  }, [])
})
</script>

<template>
  <div
    v-if="calendarSummariesWithWarning.length > 0"
    :class="$style.warningSummaries"
  >
    <div :class="$style.warningSummaries__title">
      修正が必要な日程調整カレンダー
    </div>
    <p :class="$style.warningSummaries__description">
      以下のカレンダーは、アカウント連携済み担当者が割り当てられていないため利用できません。<br />
      再設定するか、カレンダーを非公開にすることを強く推奨します。
    </p>
    <ul :class="$style.warningSummaries__list">
      <li
        v-for="summary in calendarSummariesWithWarning"
        :key="summary.calendarSummary.guid"
        :class="$style.warningSummaries__item"
      >
        <WarningCalendarSummaryViewListElement
          :warning-calendar-summary="summary.calendarSummary"
          :warning="summary.warning"
        />
      </li>
    </ul>
  </div>
</template>

<style lang="scss" module>
.warningSummaries {
  display: flex;
  flex-direction: column;
  padding: 16px;
  border: 1px solid $color-border-accent-red;
  border-radius: $border-radius-v2-inner;
  gap: 8px;

  &__title {
    font-size: 16px;
    font-weight: bold;
  }

  &__list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: 1fr;
    gap: 8px;
  }

  &__item {
    display: flex;
    align-items: stretch;
    justify-content: stretch;
  }
}
</style>
